import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  BriefcaseBusiness,
  Check,
  FileText,
  Lock,
  ShieldCheck,
  Users,
  X,
} from 'lucide-react';
import { submitEarlyAccessLead } from '../../lib/early-access';
import { formatUsd, useFunnelPriceCatalog } from '../../lib/funnel-prices';
import { FunnelPriceNotice } from '../../components/marketing/funnel/FunnelPriceNotice';

const CTA_LABEL = 'Get Early Access';

function FunnelCta({ className = '' }: { className?: string }) {
  return (
    <a
      href="#early-access"
      className={`inline-flex items-center justify-center gap-2 rounded-md bg-brand-coral px-6 py-3.5 text-sm font-bold text-white shadow-[0_18px_40px_rgba(254,89,99,0.28)] transition hover:-translate-y-0.5 hover:bg-[#ff6b72] focus:outline-none focus:ring-2 focus:ring-brand-coral/70 focus:ring-offset-2 focus:ring-offset-[#080a0f] ${className}`}
    >
      {CTA_LABEL}
      <ArrowRight className="h-4 w-4" />
    </a>
  );
}

function Section({
  id,
  children,
  className = '',
}: {
  id?: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section id={id} className={`border-t border-white/10 px-5 py-16 md:px-8 md:py-24 ${className}`}>
      <div className="mx-auto max-w-5xl">{children}</div>
    </section>
  );
}

function SectionHeading({ children }: { children: ReactNode }) {
  return <h2 className="max-w-3xl text-3xl font-bold leading-tight text-white md:text-5xl">{children}</h2>;
}

function Body({ children, className = '' }: { children: ReactNode; className?: string }) {
  return <div className={`space-y-5 text-base leading-8 text-slate-300 md:text-lg ${className}`}>{children}</div>;
}

const costOptions = [
  ['Hire more people.', 'Means recruiting, training, managing, payroll - more time you do not have to spend.'],
  ['Hire a VA.', 'Means writing SOPs you have not written and supervising work that should run itself.'],
  ['Use more software.', 'Means evaluating tools, integrating systems, and paying for things that solve 30% of one problem.'],
  ['Use AI.', 'Means chatting with ChatGPT, getting advice, copy-pasting, and doing the work yourself anyway.'],
];

const jobs = [
  ['Customer support', 'She reads every email, drafts routine replies, and flags the ones that need you.'],
  ['Sales follow-up', 'She follows up with every lead, books calls, and updates your CRM after each touch.'],
  ['Office management', 'She watches your inbox, calendar, and projects - then tells you what needs attention.'],
  ['Content writing', 'She drafts newsletters, social posts, and follow-up sequences in your voice.'],
  ['Reporting', 'She pulls your numbers, writes the summary, and emails it every Monday morning.'],
];

const audience = [
  ['For agencies', 'Turn client calls into project briefs. Draft campaign ideas from intake forms. Repurpose long-form content into social posts. Prepare client reporting summaries. Track follow-ups and project handoffs.'],
  ['For consultants', 'Summarize discovery calls. Draft proposals and scopes of work. Research client industries. Prepare meeting agendas. Build reusable client delivery templates.'],
  ['For coaches and course creators', 'Triage student questions. Draft weekly emails. Repurpose course content. Prepare onboarding sequences. Generate promotional copy in your voice.'],
  ['For solo founders', 'Organize ideas into execution plans. Draft outreach. Research leads. Prepare content calendars. Document repeatable processes.'],
  ['For operations-heavy businesses', 'Process intake requests. Summarize internal updates. Route tasks. Draft SOPs. Monitor recurring workflows.'],
];

const comparisons = [
  ['ChatGPT', 'An AI workforce', 'One drafts emails you still have to send. The other answers them.'],
  ['Zapier', 'An AI workforce', 'One moves data between tools. The other makes decisions and takes action.'],
  ['Your CRM', 'An AI workforce', 'One is a database. The other is a sales assistant who never forgets to follow up.'],
  ['Your project tool', 'An AI workforce', 'One is a list. The other gets it done.'],
  ['A virtual assistant', 'An AI workforce', 'One costs $2,000+ a month and needs to be hired, trained, and managed. The other shows up already knowing your business.'],
];

const included = [
  ['A guided conversation with an AI consultant.', 'Not a quiz. A real conversation that learns how your business operates, where work piles up, and what is eating your time. Goes as deep as you want it to. Most owners come back to it two or three times over a few days.'],
  ['Your AI Workforce Blueprint.', 'A written plan defining the jobs your AI employee should hold, the skills each job uses, and the order to set them up in. Specific to your business. Yours to keep.'],
  ['Clear next steps for implementation.', 'After your Blueprint is built, we walk you through your options for putting it to work. Manual implementation, automatic deployment, or somewhere in between - whichever path fits your business.'],
];

const faqs = [
  ['Is this just ChatGPT in a different wrapper?', 'No. ChatGPT chats. RoleForge produces a specific plan for the jobs your AI employee should hold, what she should do, and how those jobs fit together. The output is "here is your AI workforce" not "here is some text you can copy."'],
  ['What kind of business does this work for?', 'Service-based businesses between $1M-$10M in revenue: agencies, consultancies, professional services, coaches, creators, small B2B firms. Anywhere the work is mostly communication, coordination, follow-up, and content. If your business is heavily physical, she can help with less of it directly - but the quotes, scheduling, and follow-up are still fair game.'],
  ['Do I have to install anything?', 'No. RoleForge runs in your browser.'],
  ['What if I do not know what jobs to give her?', 'That is the entire point of the Blueprint. The AI consultant interviews you about your business and recommends the specific jobs she should hold for your specific situation. You do not bring expertise - you bring your business.'],
  ['How long does the Blueprint take?', 'As long as you want. Most owners come back to it two or three times across a few days. The more you put in, the more specific the output. There is no rush.'],
  ['What happens after I get my Blueprint?', 'We will show you your options for implementing it - including a one-time upgrade offer that gives you the fastest path from plan to a working AI employee.'],
  ['What if it does not work for me?', 'We would rather make it right than send you away. If during your 14 days you do not feel you got the clarity and value we promised, tell us. We will work with you to make it right - extend your access, set up a working session, help you build what you came for. Email us and we will take care of it.'],
  ['Do I need to be technical to use this?', 'No. If you can have a conversation, you can use RoleForge.'],
  ['Who is behind RoleForge?', 'Built by Dustin, who has run a small business IT services company since 2008 and an AI services agency for the last few years. Not a venture-funded startup chasing valuation - a profitable business with skin in the game.'],
  ['Does this replace my team?', 'No. It removes the work your team should not be doing in the first place - the coordination, follow-up, and admin that fills your team days. Most owners use this to make their existing team more effective, not to replace them.'],
];

function StickyCta() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 560);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <div
      className={`fixed inset-x-0 bottom-0 z-40 border-t border-white/10 bg-[#080a0f]/92 px-4 py-3 backdrop-blur-xl transition duration-300 ${
        visible ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'
      }`}
    >
      <div className="mx-auto flex max-w-5xl flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-sm font-semibold text-white">AI Workforce Blueprint</p>
          <p className="text-xs text-slate-400">Join the early access list. Joining is free.</p>
        </div>
        <FunnelCta className="w-full sm:w-auto" />
      </div>
    </div>
  );
}

function EarlyAccessSection({ blueprintPrice }: { blueprintPrice: string }) {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const lead = {
      name: String(form.get('name') || '').trim(),
      email: String(form.get('email') || '').trim(),
      company: String(form.get('company') || '').trim(),
      revenue: String(form.get('revenue') || '').trim(),
    };

    setSubmitting(true);
    setError(null);

    try {
      const result = await submitEarlyAccessLead(lead);
      localStorage.setItem(
        'roleforge-early-access-lead',
        JSON.stringify({
          ...lead,
          capturedAt: new Date().toISOString(),
          submittedVia: result.submittedVia,
          submissionId: result.submissionId,
        }),
      );
      setSubmitted(true);
    } catch {
      setError('Something went wrong. Please try again in a moment.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Section id="early-access" className="bg-[#0b0f16] scroll-mt-24">
      <div className="grid gap-10 lg:grid-cols-[0.95fr_1.05fr] lg:items-start">
        <div>
          <p className="text-sm font-semibold uppercase text-brand-teal">Early access</p>
          <SectionHeading>Not buying today? Get on the early access list.</SectionHeading>
          <p className="mt-6 max-w-2xl text-lg leading-8 text-slate-300">
            The AI Workforce Blueprint is open now - you can{' '}
            <Link to="/blueprint" className="font-semibold text-brand-teal underline decoration-brand-teal/40 underline-offset-4 hover:decoration-brand-teal">
              get yours now for {blueprintPrice}
            </Link>
            . If you would rather wait, join the early access list and we will email you when there
            is something worth telling you.
          </p>
          <div className="mt-8 grid gap-3 text-sm text-slate-300">
            {[
              'Joining the list is free - we do not ask for a card.',
              'Early users get first access to the guided Blueprint experience.',
              'We will prioritize service businesses where communication, follow-up, and coordination are the bottleneck.',
            ].map((item) => (
              <div key={item} className="flex gap-3">
                <Check className="mt-0.5 h-4 w-4 shrink-0 text-brand-teal" />
                <span>{item}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-lg border border-white/10 bg-[#0e131b] p-4 shadow-2xl shadow-black/40">
          <div className="rounded-md border border-white/10 bg-[#090c12] p-5 md:p-6">
            {submitted ? (
              <div className="py-8 text-center">
                <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-lg border border-brand-teal/25 bg-brand-teal/10">
                  <Check className="h-7 w-7 text-brand-teal" />
                </div>
                <h3 className="mt-5 text-2xl font-bold text-white">You are on the early access list.</h3>
                <p className="mt-3 text-sm leading-6 text-slate-400">
                  Thanks for raising your hand. We will email you when there is something worth
                  telling you.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label htmlFor="early-name" className="mb-2 block text-sm font-semibold text-white">
                    Name
                  </label>
                  <input
                    id="early-name"
                    name="name"
                    autoComplete="name"
                    className="w-full rounded-md border border-white/15 bg-[#080a0f] px-4 py-3.5 text-white placeholder-slate-600 transition focus:border-brand-coral/70 focus:outline-none focus:ring-2 focus:ring-brand-coral/35"
                    placeholder="Your name"
                  />
                </div>
                <div>
                  <label htmlFor="early-email" className="mb-2 block text-sm font-semibold text-white">
                    Work email
                  </label>
                  <input
                    id="early-email"
                    name="email"
                    type="email"
                    required
                    autoComplete="email"
                    className="w-full rounded-md border border-white/15 bg-[#080a0f] px-4 py-3.5 text-white placeholder-slate-600 transition focus:border-brand-coral/70 focus:outline-none focus:ring-2 focus:ring-brand-coral/35"
                    placeholder="you@company.com"
                  />
                </div>
                <div>
                  <label htmlFor="early-company" className="mb-2 block text-sm font-semibold text-white">
                    Business name
                  </label>
                  <input
                    id="early-company"
                    name="company"
                    autoComplete="organization"
                    className="w-full rounded-md border border-white/15 bg-[#080a0f] px-4 py-3.5 text-white placeholder-slate-600 transition focus:border-brand-coral/70 focus:outline-none focus:ring-2 focus:ring-brand-coral/35"
                    placeholder="Company"
                  />
                </div>
                <div>
                  <label htmlFor="early-revenue" className="mb-2 block text-sm font-semibold text-white">
                    Monthly revenue
                  </label>
                  <select
                    id="early-revenue"
                    name="revenue"
                    defaultValue=""
                    className="w-full rounded-md border border-white/15 bg-[#080a0f] px-4 py-3.5 text-white transition focus:border-brand-coral/70 focus:outline-none focus:ring-2 focus:ring-brand-coral/35"
                  >
                    <option value="" disabled>
                      Select range
                    </option>
                    <option>Under $25K/mo</option>
                    <option>$25K-$50K/mo</option>
                    <option>$50K-$100K/mo</option>
                    <option>$100K+/mo</option>
                  </select>
                </div>
                <button
                  type="submit"
                  disabled={submitting}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-brand-coral px-6 py-4 text-sm font-bold text-white shadow-[0_18px_40px_rgba(254,89,99,0.28)] transition hover:-translate-y-0.5 hover:bg-[#ff6b72] focus:outline-none focus:ring-2 focus:ring-brand-coral/70 disabled:cursor-not-allowed disabled:opacity-70 disabled:hover:translate-y-0"
                >
                  {submitting ? 'Requesting Access...' : 'Request Early Access'}
                  <ArrowRight className="h-4 w-4" />
                </button>
                {error ? (
                  <p className="rounded-md border border-brand-coral/25 bg-brand-coral/10 px-4 py-3 text-center text-sm text-brand-coral" role="alert">
                    {error}
                  </p>
                ) : null}
                <p className="text-center text-xs leading-5 text-slate-500">
                  Joining the list is free. We will only email you about RoleForge early access.
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </Section>
  );
}

export function LandingPage() {
  const prices = useFunnelPriceCatalog();
  const blueprintPrice = formatUsd(prices.blueprintCents);

  return (
    <>
      <section className="relative overflow-hidden px-5 pb-16 pt-20 md:px-8 md:pb-24 md:pt-28">
        <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(255,255,255,0.04)_1px,transparent_1px),linear-gradient(rgba(255,255,255,0.035)_1px,transparent_1px)] bg-[size:72px_72px] opacity-35" />
        <div className="relative mx-auto flex max-w-4xl flex-col items-center text-center">
          <h1 className="max-w-4xl text-5xl font-bold leading-[0.98] text-white md:text-7xl">
            Your business is growing — and you're still answering emails at midnight.
          </h1>
          <p className="mt-7 max-w-2xl text-lg leading-8 text-slate-300 md:text-xl">
            The work does not stop because you are tired. It stops when you stop. RoleForge maps
            the jobs an AI employee can take off your plate - then gives you a
            deployable plan to actually put her to work. {blueprintPrice}.
          </p>
          <FunnelPriceNotice snapshot={prices} />
          <div className="mt-9 flex flex-col items-center justify-center gap-4 sm:flex-row">
            <FunnelCta />
            <div className="flex items-center gap-2 text-sm text-slate-400">
              <Lock className="h-4 w-4 text-brand-teal" />
              Joining the early access list is free.
            </div>
          </div>
        </div>
      </section>

      <Section>
        <div className="grid gap-10 lg:grid-cols-[0.9fr_1.1fr]">
          <SectionHeading>You did not start this business to be its top employee.</SectionHeading>
          <Body>
            <p>You started it to build something. To be the boss. To finally have control over your time.</p>
            <p>
              Instead you are the one answering customer emails at 11pm. Following up with leads nobody
              else will touch. Cleaning the CRM after your VA gives up on it again. Writing reports on
              Sundays because that is the only quiet time you have.
            </p>
            <p className="text-xl font-semibold text-white">And you are still the bottleneck.</p>
            <p>
              The business grew. Your time did not. Every new customer is another email you will answer.
              Every new lead is another follow-up you will forget. Every month is another round of putting
              out fires you have put out before.
            </p>
            <div className="grid gap-3 pt-3">
              {costOptions.map(([title, body]) => (
                <div key={title} className="rounded-md border border-white/10 bg-white/[0.035] p-4">
                  <strong className="text-white">{title}</strong> <span className="text-slate-400">{body}</span>
                </div>
              ))}
            </div>
            <p>None of it actually moves work off your desk. It just changes what is on your desk.</p>
          </Body>
        </div>
      </Section>

      <Section className="bg-[#0b0f16]">
        <div className="grid gap-10 lg:grid-cols-[0.85fr_1.15fr]">
          <div>
            <SectionHeading>What if one AI employee actually did the work?</SectionHeading>
            <p className="mt-6 max-w-2xl text-lg leading-8 text-slate-300">
              Not an advisor. Not an assistant that needs supervision. Not a chatbot that helps you draft while you do the writing.
            </p>
          </div>
          <div className="grid gap-3">
            {jobs.map(([title, body]) => (
              <div key={title} className="flex gap-4 rounded-md border border-white/10 bg-[#111720] p-4">
                <BriefcaseBusiness className="mt-1 h-5 w-5 shrink-0 text-brand-coral" />
                <p className="text-slate-300">
                  <strong className="text-white">{title}:</strong> {body}
                </p>
              </div>
            ))}
          </div>
        </div>
        <Body className="mt-10 max-w-3xl">
          <p className="text-xl font-semibold text-white">This is what AI is finally able to do. Not advise. Not summarize. Actually do the work.</p>
          <p>
            The catch? Until now, you needed a developer or a $20,000 consultant to figure out which
            jobs to hand her first, what each one should look like, and how to make it actually work for your business.
          </p>
          <p className="font-semibold text-white">That is what RoleForge is for.</p>
        </Body>
      </Section>

      <Section>
        <div className="grid gap-10 lg:grid-cols-[0.9fr_1.1fr]">
          <div>
            <SectionHeading>RoleForge maps your AI workforce. Specifically. For your business.</SectionHeading>
            <p className="mt-6 text-lg leading-8 text-slate-300">
              For {blueprintPrice}, you sit down with an AI consultant. Not a quiz. Not a form. A real conversation
              that goes as deep as you want it to.
            </p>
          </div>
          <div className="space-y-4">
            {[
              ['The jobs your AI employee should hold.', 'Each job, named and described. What she is responsible for. How autonomous she should be. The personality she wears for it. The level of judgment she is trusted with.'],
              ['The skills each job uses.', 'What tools she needs for each job. What inputs she reads. What outputs she produces. How it fits into the work you already do.'],
              ['The order to set them up in.', 'Which job buys back the most of your time first, based on what is eating it today. A short, prioritized plan — not twenty ideas you will never touch.'],
            ].map(([title, body]) => (
              <div key={title} className="rounded-md border border-white/10 bg-white/[0.035] p-5">
                <FileText className="mb-4 h-5 w-5 text-brand-teal" />
                <h3 className="font-semibold text-white">{title}</h3>
                <p className="mt-2 text-sm leading-7 text-slate-400">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </Section>

      <Section id="path" className="bg-[#0b0f16]">
        <div className="max-w-3xl">
          <SectionHeading>A plan you cannot act on is not a plan.</SectionHeading>
          <Body className="mt-7">
            <p>A reasonable thing to wonder: <em className="text-white">Okay, I get a plan. Then what?</em></p>
            <p>
              Your Blueprint is built to be acted on. Whether you implement it manually using tools
              you already have - like Claude or ChatGPT - or have it set up for you automatically,
              you will have a clear path from plan to a working AI employee.
            </p>
            <p>We walk you through your options after the Blueprint is complete. Nothing extra to figure out, nothing hidden.</p>
            <p className="font-semibold text-white">What you will not get is a 40-page PDF that sits in your downloads folder.</p>
            <FunnelCta />
          </Body>
        </div>
      </Section>

      <Section>
        <SectionHeading>See yourself in here?</SectionHeading>
        <p className="mt-5 max-w-3xl text-lg leading-8 text-slate-300">
          RoleForge works for businesses where most of the work is communication, coordination,
          follow-up, and content - the kind of work an AI employee can actually take on.
        </p>
        <div className="mt-10 grid gap-4 md:grid-cols-2">
          {audience.map(([title, body]) => (
            <div key={title} className="rounded-md border border-white/10 bg-white/[0.035] p-5">
              <Users className="mb-4 h-5 w-5 text-brand-coral" />
              <h3 className="font-semibold text-white">{title}</h3>
              <p className="mt-2 text-sm leading-7 text-slate-400">{body}</p>
            </div>
          ))}
        </div>
      </Section>

      <Section className="bg-[#0b0f16]">
        <SectionHeading>Why just use ChatGPT is not the same thing</SectionHeading>
        <div className="mt-10 overflow-hidden rounded-lg border border-white/10">
          {comparisons.map(([from, to, body]) => (
            <div key={from} className="grid gap-3 border-b border-white/10 bg-[#0f141d] p-5 last:border-b-0 md:grid-cols-[0.8fr_0.8fr_1.4fr]">
              <p className="font-semibold text-slate-400">{from}</p>
              <p className="font-semibold text-brand-teal">{to}</p>
              <p className="text-sm leading-7 text-slate-300">{body}</p>
            </div>
          ))}
        </div>
        <p className="mt-8 max-w-3xl text-lg leading-8 text-slate-300">
          RoleForge tells you which jobs your AI employee should take on. Then it shows you how to put her to work.
        </p>
      </Section>

      <Section>
        <div className="grid gap-6 md:grid-cols-2">
          <div className="rounded-lg border border-brand-teal/20 bg-brand-teal/10 p-6">
            <h2 className="text-2xl font-bold text-white">This is for you if:</h2>
            <ul className="mt-6 space-y-4 text-slate-300">
              {[
                'You run a service business between $1M and $10M in revenue',
                'You are the bottleneck - most work routes through you',
                'You have tried AI tools and felt they helped a little, but never enough',
                'You are done buying software that does not actually move work off your desk',
                "You want practical AI use cases for your business, not someone else's business",
              ].map((item) => (
                <li key={item} className="flex gap-3"><Check className="mt-1 h-5 w-5 shrink-0 text-brand-teal" />{item}</li>
              ))}
            </ul>
          </div>
          <div className="rounded-lg border border-white/10 bg-white/[0.035] p-6">
            <h2 className="text-2xl font-bold text-white">This is NOT for you if:</h2>
            <ul className="mt-6 space-y-4 text-slate-400">
              {[
                'You are looking for an AI chatbot for your website',
                'You are a developer who wants to build agent workflows yourself',
                'You are an enterprise buyer expecting a procurement process and sales cycle',
                'You are under $100K in revenue and looking for free tools to get started',
              ].map((item) => (
                <li key={item} className="flex gap-3"><X className="mt-1 h-5 w-5 shrink-0 text-brand-coral" />{item}</li>
              ))}
            </ul>
          </div>
        </div>
      </Section>

      {/* Issue #1215: this column used to hold a dashed placeholder box whose
          customer-visible text was a design-time TODO addressed to our own
          team — live on the site root, next to the personal credibility story
          whose whole job is to make the buyer trust a real human. No headshot
          has been supplied, so the media column is gone and the copy runs full
          width. When the owner provides the photo, restore a real media column
          here; never reinstate a placeholder. Guarded by
          LandingPage.copy.test.ts. */}
      <Section className="bg-[#0b0f16]">
        <div className="max-w-3xl">
          <Body>
            <SectionHeading>Built by someone who has been cleaning up after small business software for 18 years.</SectionHeading>
            <p>I am Dustin. I built RoleForge.</p>
            <p>
              I have spent the last 18 years running an IT services company for small businesses -
              the kind of business that gets called when something stops working and someone has to fix it.
              I have watched owners get sold a hundred different tools that promised to change their business
              and delivered another monthly fee.
            </p>
            <p>
              The AI tools rolling out right now? Most of them are the same story. A chat box. Some prompts.
              A productivity promise that ends with the owner still doing the work.
            </p>
            <p>
              I built RoleForge because I am tired of recommending things that do not work. This is what I wish
              I had had to give my own clients for the last decade.
            </p>
          </Body>
        </div>
      </Section>

      <Section>
        <SectionHeading>What this actually looks like in a real business</SectionHeading>
        <div className="mt-10 grid gap-4 lg:grid-cols-3">
          {[
            ['Agency, about 12 people', 'We use this internally to run the operations of our own agency. The AI workforce handles client communications, project status updates, internal reporting, and follow-ups across every active engagement.'],
            ['Online course creator', 'One AI employee handles student questions, drafts the weekly email, and flags the ones that actually need human attention.'],
            ['Small church or nonprofit', 'An AI workforce handles communication with the congregation, manages event responses, drafts weekly updates, and flags what needs a human.'],
          ].map(([title, body]) => (
            <figure key={title} className="rounded-lg border border-white/10 bg-white/[0.035] p-5">
              <blockquote className="text-sm leading-7 text-slate-300">{body}</blockquote>
              <figcaption className="mt-5 border-t border-white/10 pt-4 text-sm font-semibold text-white">{title}</figcaption>
            </figure>
          ))}
        </div>
        <div className="mt-8">
          <FunnelCta />
        </div>
      </Section>

      <Section id="included" className="bg-[#0b0f16]">
        <SectionHeading>Your AI Workforce Blueprint, delivered.</SectionHeading>
        <div className="mt-10 grid gap-4">
          {included.map(([title, body], index) => (
            <div key={title} className="grid gap-4 rounded-lg border border-white/10 bg-[#111720] p-5 md:grid-cols-[80px_1fr]">
              <div className="text-4xl font-bold text-brand-coral">{index + 1}</div>
              <div>
                <h3 className="text-xl font-semibold text-white">{title}</h3>
                <p className="mt-2 leading-7 text-slate-400">{body}</p>
              </div>
            </div>
          ))}
        </div>
        <p className="mt-7 text-lg font-semibold text-white">No subscriptions. No surprise charges. {blueprintPrice}, one time.</p>
      </Section>

      <Section id="faq">
        <SectionHeading>Questions people ask before buying</SectionHeading>
        <div className="mt-10 divide-y divide-white/10 rounded-lg border border-white/10">
          {faqs.map(([q, a]) => (
            <details key={q} className="group bg-white/[0.025] p-5 open:bg-white/[0.045]">
              <summary className="flex cursor-pointer list-none items-center justify-between gap-4 font-semibold text-white">
                {q}
                <span className="text-brand-coral group-open:rotate-45">+</span>
              </summary>
              <p className="mt-4 max-w-3xl text-sm leading-7 text-slate-400">{a}</p>
            </details>
          ))}
        </div>
      </Section>

      <Section className="bg-[#0b0f16]">
        <div className="grid gap-8 lg:grid-cols-[0.9fr_1.1fr] lg:items-center">
          <SectionHeading>Two options.</SectionHeading>
          <Body>
            <p><strong className="text-white">Option 1:</strong> Close this tab. Go back to answering emails. Write yourself a note that says look into AI for the business someday. Do it again next month.</p>
            <p><strong className="text-white">Option 2:</strong> Spend {blueprintPrice}. Find out exactly what an AI workforce would do for your business. Walk away with a real plan you can act on.</p>
            <p>The second option costs less than the dinner you will have tonight. It either gives you clarity or it does not, and you will know inside an hour.</p>
            <div className="pt-3">
              <FunnelCta />
              <p className="mt-4 flex items-start gap-2 text-sm leading-6 text-slate-400">
                <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-brand-teal" />
                <span>
                  The Blueprint is open now -{' '}
                  <Link to="/blueprint" className="font-semibold text-brand-teal underline decoration-brand-teal/40 underline-offset-4 hover:decoration-brand-teal">
                    get yours for {blueprintPrice}
                  </Link>
                  . Not ready to buy today? Join the early access list instead - joining is free.
                </span>
              </p>
            </div>
          </Body>
        </div>
      </Section>

      <EarlyAccessSection blueprintPrice={blueprintPrice} />

      <StickyCta />
    </>
  );
}
